import { Link } from 'react-router-dom';

/**
 * "Which revenue is this?" — ONE answer, shared by the clients LIST and the
 * client PAGE, in the same words.
 *
 * ⚠ THE APP HAS TWO REVENUE QUESTIONS AND THEY HAVE DIFFERENT ANSWERS. Since
 * 2026-08-13 the FIRM's revenue is **cash collected** — a `Payment` on the day
 * it arrives, one predicate (`backend/src/modules/pnl/revenue.ts`), read by the
 * P&L, the dashboard and the cash report alike. A CLIENT's revenue on these two
 * screens is something else: a valuation of the **work** — a fixed bid's
 * contract value, hourly work at hours × rate, a monthly fee as invoiced —
 * which is exactly what `/agreements` prints per row and what these screens roll
 * up. The two are never reconciled; on seed data they are $1,017,959 of work
 * against $1,004,839 of cash, and an unlabelled figure a click apart from the
 * other reads as a second, wrong answer to one question.
 *
 * ⚠ **WHERE IT GOES CHANGED ON 2026-08-13.** This used to be a full sentence
 * printed under the totals of both screens, plus a `title` on every figure.
 * Jesse, reading the real screens: *"don't need explanations on clients view …
 * if this description needs to be available make it in (i) icon info popups."*
 *
 * So the split is now: the SHORT qualifier stays on screen (`..._NOTE_SHORT` —
 * the three words that stop a reader taking this for the firm's revenue), and
 * the paragraph, the three bases and the cross-screen comparison live in
 * `<RevenueBasisInfo/>`, behind the one `InfoHint`. **A label, not a lesson.**
 * Both screens still take their words from here so they cannot drift.
 */

/** The SHORT qualifier that stays on screen beside the figure. */
export const REVENUE_BASIS_NOTE_SHORT = 'value of work, not cash';

/**
 * ⚠ **THIS SCREEN MAY NOT COUNT THE BASES.** `/agreements` prints "N bases" on
 * its footer because its rows carry `revenueSource` and the count is real.
 * `GET /api/clients` ships no such field, so a count here would be a hardcoded
 * guess about the contents of a total — and a screen that miscounts itself is
 * worse than one that says less. The qualifier alone does the necessary work:
 * it stops the figure being read as the firm's cash revenue.
 */

/** The explanation — the body of the (i) on any figure using this basis. */
export function RevenueBasisInfo() {
  return (
    <>
      <p>
        What the WORK is worth, not what the business earned. Three bases add up in one figure: a
        fixed bid’s contract value, hourly work at hours × rate, and a monthly fee as invoiced —{' '}
        <Link to="/agreements" className="font-semibold text-accent-700 hover:underline">
          /agreements
        </Link>{' '}
        names which on every row.
      </p>
      <p className="mt-2">
        The firm’s revenue is CASH COLLECTED (
        <Link to="/reports/pnl" className="font-semibold text-accent-700 hover:underline">
          Profit &amp; Loss
        </Link>
        ); this reads higher by everything delivered or billed and not yet paid for. The two answer
        different questions and are never reconciled.
      </p>
    </>
  );
}
